import { useEffect, useRef, useState } from 'react'

import { useInfiniteQuery } from '@api/hooks'
import Callout from '@components/Callout/Callout'
import DialogTitle from '@components/mui/DialogTitle'
import SearchInput from '@components/SearchInput/SearchInput'
import { Manufacturer } from '@domain'
import { faFaceSadSweat, faLightbulb, faUsers } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	LinearProgress,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import ManufacturersApi, { ManufacturersKey } from '@services/manufacturers.api'
import { AxiosError } from 'axios'
import Highlight from 'react-highlighter'
import { useInView } from 'react-intersection-observer'

type Props = {
	name: string
	open: boolean
	onClose: () => void
	onSave: (results: Manufacturer[]) => void
}

export default function SearchListInputDialog({ name, open, onClose, onSave }: Props) {
	const [searchValue, setSearchValue] = useState('')
	const [checked, setChecked] = useState([])
	const { ref: nextPageRef, inView } = useInView()
	const theme = useTheme()
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

	const {
		data,
		isLoading,
		refetch,
		isStale,
		isRefetching,
		isFetchingNextPage,
		fetchNextPage,
		error,
	} = useInfiniteQuery({
		queryKey: ManufacturersKey.list({ input: searchValue }),
		queryFn: ({ pageParam }) =>
			ManufacturersApi.list({ input: searchValue, page: pageParam, pageSize: 50 }),
		enabled: searchValue.length > 1,
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			const { page, totalPages } = lastPage.pagination
			return page < totalPages ? page + 1 : null
		},
		throwOnError: false,
	})

	const manufacturers = data?.pages.flatMap((page) => page.data) ?? null
	const errorMessage = error ? (error as AxiosError<ApiErrorRes>).response?.data?.message : null

	// Solution found online for handling autofocus issue in Dialog
	// I believe issue stems from Dialog keepMounted property
	const ref = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (ref.current && open) {
			const inputEl: HTMLInputElement = ref?.current?.querySelector?.(`#${name}`)
			// Without timeout '/' is immediately typed into field when hotkey is used to open
			if (inputEl) {
				const timeoutId = setTimeout(() => inputEl.focus(), 0)
				return () => clearTimeout(timeoutId)
			}
		}
	}, [open, name])

	useEffect(() => {
		if (inView) fetchNextPage()
	}, [inView, fetchNextPage])

	useEffect(() => {
		if (open && isStale) {
			refetch()
		}
	}, [isStale, open, refetch])

	const handleSearchChange = (value: string) => {
		setSearchValue(value)
	}

	const handleToggle = (item) => () => {
		const currentIndex = checked.indexOf(item)
		const newChecked = [...checked]

		if (currentIndex === -1) {
			newChecked.push(item)
		} else {
			newChecked.splice(currentIndex, 1)
		}

		setChecked(newChecked)
	}

	return (
		<Dialog
			closeAfterTransition={false}
			open={open}
			ref={ref}
			scroll="paper"
			keepMounted
			fullScreen={fullScreen}
			fullWidth
			onClose={onClose}
			sx={{
				'& .MuiDialog-container': {
					alignItems: 'flex-start',
				},
			}}
		>
			{isRefetching && <LinearProgress color="secondary" />}
			<DialogTitle
				onClose={() => {
					setChecked([])
					onClose()
				}}
			>
				Manufacturers
			</DialogTitle>
			<Stack
				sx={{
					px: 3,
					pb: 2,
				}}
				gap={2}
			>
				{errorMessage ? (
					<Alert severity="error">{errorMessage}</Alert>
				) : (
					<Callout icon={<FontAwesomeIcon icon={faLightbulb} />}>
						Type a name or PLID and press Enter
					</Callout>
				)}

				<SearchInput
					id={name}
					error={!!searchValue && searchValue.length < 2}
					errorText="Type at least 2 characters to search!"
					value={searchValue}
					onChange={handleSearchChange}
					searchOnEnter
					sx={{ position: 'sticky' }}
				/>
			</Stack>
			<DialogContent sx={{ pt: 0 }}>
				<Stack>
					{!manufacturers && !isLoading && (
						<Stack mt={2} gap={2} alignItems="center">
							<FontAwesomeIcon color={theme.palette.primary.main} size="2x" icon={faUsers} />
							<Typography>Type a name and press &quot;enter&quot; to search</Typography>
						</Stack>
					)}
					{isLoading && (
						<Box display="flex" justifyContent="center">
							<CircularProgress color="secondary" />
						</Box>
					)}
					{manufacturers && !manufacturers.length && (
						<Stack mt={2} gap={2} alignItems="center">
							<FontAwesomeIcon color={theme.palette.primary.main} size="2x" icon={faFaceSadSweat} />
							<Typography>Sorry, we couldn&apos;t find a match</Typography>
						</Stack>
					)}
					{manufacturers && manufacturers.length > 0 && (
						<List sx={{ width: '100%' }}>
							{manufacturers.map((item, i) => {
								const labelId = `checkbox-list-label-${item.id}`
								const indexToFetchNextPage = Math.floor(manufacturers.length * 0.7)
								return (
									<Box key={item.id} ref={i === indexToFetchNextPage ? nextPageRef : null}>
										<ListItem disablePadding>
											<ListItemButton onClick={handleToggle(item)} dense>
												<ListItemIcon>
													<Checkbox
														edge="start"
														checked={checked.includes(item)}
														disableRipple
														inputProps={{ 'aria-labelledby': labelId }}
													/>
												</ListItemIcon>
												<ListItemText
													sx={{ width: '100%' }}
													id={labelId}
													primary={<Highlight search={searchValue}>{item.name}</Highlight>}
													secondary={
														<Stack
															width="100%"
															direction="row"
															justifyContent="space-between"
															gap={1}
														>
															<Highlight search={searchValue}>{item.name || ''}</Highlight>
															<Highlight search={searchValue}>
																{`PLID: ${item.plid || ''}`}
															</Highlight>
														</Stack>
													}
												/>
											</ListItemButton>
										</ListItem>
										<Divider />
									</Box>
								)
							})}
						</List>
					)}
					{isFetchingNextPage && (
						<CircularProgress sx={{ alignSelf: 'center' }} color="secondary" />
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					disabled={!checked.length}
					onClick={() => {
						onSave(checked)
						setChecked([])
						onClose()
					}}
					variant="contained"
				>
					Add Manufacturers
				</Button>
			</DialogActions>
		</Dialog>
	)
}
